/**
 * VRG-727 — the two money words, each defined ONCE (Spencer, 2026-10-20).
 *
 * ── WON ──────────────────────────────────────────────────────────────────────────────────
 * A company is WON at its FIRST PAID INVOICE: the earliest invoice Stripe marked paid with
 * `amount_paid` above $0, in LIVE mode, on a REAL company (kind `standard`, not test, not
 * example). Won never reverts: nothing automatic moves a deal out of Won, and a company that
 * later stops paying is still a company that once paid (it is "Not paying", below).
 *
 * `firstPaidInvoiceWonAt` is the ONE function that says so, and both consumers call it:
 *   - the Sales sync's `paid_client` evidence (`sales-sync.service.ts` `evidenceFromRow`), which
 *     moves the deal to Won dated at the invoice;
 *   - `companies.became_client_at` (`ClientConversionService.markBecameClient`), stamped with the
 *     same instant, which is what the VRG-639 `became_client` PostHog event carries.
 * So the two can never disagree about whether, or when, a company became a client.
 * (`sales-paying.guard.spec.ts` pins it.)
 *
 * ── PAYING NOW ───────────────────────────────────────────────────────────────────────────
 * A subscription is PAYING NOW when it is a live-mode, active (not trialing), real subscription
 * AND a live paid invoice above $0 for it (or, naming no subscription, for its company) landed
 * in the last PAYING_INVOICE_WINDOW_DAYS. The rule is `payingVerdict` (gtm/auto-won.ts); a company
 * is paying now when any of its subscriptions is. It LAPSES: 60 days without a paid invoice and
 * the company reads "Not paying" (churned). A company that never paid reads "Never paid".
 *
 * Before VRG-727 the paying rule also counted a priced, un-comped subscription with NO invoice
 * (the "list price" leg) and drove the automatic Won. Now money has to have arrived, and the
 * automatic Won waits for a first paid invoice.
 *
 * ── LIVE MODE ────────────────────────────────────────────────────────────────────────────
 * An invoice or subscription with no recorded mode (`livemode` NULL: written before the mode
 * was stored) is read as the mode of the API's own Stripe key — a Stripe object can only reach
 * the account and mode of the key whose webhook endpoint received it. On prod (live key) such a
 * row is live; on dev (test key) it is test, so dev never wins or pays anything by itself.
 */

/** How far back a paid invoice still says "paying now" (Spencer, 2026-10-14: "~60 days"). */
export const PAYING_INVOICE_WINDOW_DAYS = 60;

export interface PaidInvoiceFacts {
  /** `amount_paid`, cents. */
  amount_cents: number | null;
  /** The invoice's own mode, or null when it was not recorded (read as the key's mode). */
  livemode: boolean | null;
}

export interface CompanyFacts {
  kind: unknown;
  is_test: unknown;
  is_example: unknown;
}

/** Live mode and above $0: an invoice that can make a company Won. */
export const isWinningPaidInvoice = (inv: PaidInvoiceFacts | null | undefined, ctx: {envLivemode: boolean}): boolean =>
  !!inv && typeof inv.amount_cents === 'number' && inv.amount_cents > 0 && (inv.livemode ?? ctx.envLivemode) === true;

/** A real company: standard, not test, not example. Demo and internal companies are never clients. */
export const isRealClientCompany = (c: CompanyFacts | null | undefined): boolean =>
  !!c && c.kind === 'standard' && c.is_test !== true && c.is_example !== true;

/** The company's first paid invoice, as the Won rule reads it. */
export interface FirstPaidInvoiceFacts extends PaidInvoiceFacts {
  company: CompanyFacts | null;
  /** When the invoice was paid. */
  paid_at: unknown;
}

const isoOf = (d: unknown): string | null => {
  if (d == null) return null;
  const t = d instanceof Date ? d : new Date(String(d));
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
};

/**
 * WHEN the company was won (ISO), or null when it was not: its first paid invoice counts only
 * above $0, in live mode, on a real company. THE Won rule — the Sales sync's evidence and
 * `became_client_at` both read it.
 */
export const firstPaidInvoiceWonAt = (f: FirstPaidInvoiceFacts | null | undefined, ctx: {envLivemode: boolean}): string | null => {
  if (!f || !isRealClientCompany(f.company) || !isWinningPaidInvoice(f, ctx)) return null;
  return isoOf(f.paid_at);
};

// ── The status a company carries (chips and the filter facet) ─────────────────────────────

export const PAYING_NOW_STATUSES = ['paying_now', 'not_paying', 'never_paid'] as const;
export type PayingNowStatus = (typeof PAYING_NOW_STATUSES)[number];

export const PAYING_NOW_STATUS_LABELS: Readonly<Record<PayingNowStatus, string>> = {
  paying_now: 'Paying now',
  not_paying: 'Not paying',
  never_paid: 'Never paid',
};

export const isPayingNowStatus = (v: unknown): v is PayingNowStatus =>
  typeof v === 'string' && (PAYING_NOW_STATUSES as readonly string[]).includes(v);

/**
 * A company's status: Paying now when any subscription is (`payingVerdict`); else Not paying
 * when it was ever Won (a first paid invoice: it paid, and lapsed or churned); else Never paid.
 */
export const payingNowStatus = (c: {payingNow: boolean; wonAt: string | null}): PayingNowStatus =>
  c.payingNow ? 'paying_now' : c.wonAt ? 'not_paying' : 'never_paid';
